import { For, Show, createMemo, createSignal } from "solid-js";

import { useAiModelCatalog, usePartnerAISettings, useUpdatePartnerAISettings } from "~/lib/hooks";
import type { AIModelCatalogEntry, PartnerAISettingsUpdate } from "~/lib/types";

type Props = {
  partnerId: () => string;
  // A workspace under this partner — the catalog endpoint is workspace-
  // scoped today, and any workspace under the partner returns the same
  // ScaiGrid catalog. Passing one through keeps the existing endpoint
  // reusable without adding a partner-tier catalog route.
  catalogProbeWsKey: () => string;
};

/**
 * Partner-tier AI model allowlist.
 *
 * Sits above the workspace-tier `AISettingsPanel` in the hierarchy:
 * `partner ⊇ workspace ⊇ agent`. Removing a model here blocks it in
 * every workspace under the partner (and the workspace settings panel
 * renders it greyed-out with "blocked by partner").
 *
 * Semantics:
 * - `allowed_model_ids = null` → no partner-tier restriction (workspaces
 *   may pick anything in the live ScaiGrid catalog).
 * - `allowed_model_ids = []` → AI explicitly disabled for the partner.
 * - `allowed_model_ids = [...]` → only those ids are usable downstream.
 */
export function PartnerAISettingsPanel(props: Props) {
  const settings = usePartnerAISettings(props.partnerId);
  const catalog = useAiModelCatalog(props.catalogProbeWsKey, () => true);
  const update = useUpdatePartnerAISettings(props.partnerId);

  const [unrestricted, setUnrestricted] = createSignal(true);
  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set<string>());
  const [defaultModelId, setDefaultModelId] = createSignal<string>("");
  const [budgetCents, setBudgetCents] = createSignal<string>("");
  const [error, setError] = createSignal<string | null>(null);
  const [hydrated, setHydrated] = createSignal(false);

  // One-shot hydration from server values.
  const initFromServer = () => {
    if (hydrated()) return;
    // `null` body = no row yet; we still want to mark hydrated to stop
    // re-running the initialiser.
    if (settings.isLoading) return;
    const row = settings.data;
    if (!row) {
      setUnrestricted(true);
      setSelectedIds(new Set<string>());
      setDefaultModelId("");
      setBudgetCents("");
    } else {
      const allowed = row.allowed_model_ids;
      setUnrestricted(allowed === null);
      setSelectedIds(new Set(allowed ?? []));
      setDefaultModelId(row.default_model_id ?? "");
      setBudgetCents(
        row.monthly_inference_budget_cents !== null
          ? String(row.monthly_inference_budget_cents)
          : "",
      );
    }
    setHydrated(true);
  };
  initFromServer();

  const allModels = createMemo<AIModelCatalogEntry[]>(() => {
    const catEntries = catalog.data?.models ?? [];
    const known = new Set(catEntries.map((m) => m.id));
    const orphans = Array.from(selectedIds())
      .filter((id) => !known.has(id))
      .map((id) => ({
        id,
        label: `${id} (no longer in catalog)`,
        vendor: null,
        family: null,
        context_window: null,
        input_price_per_1k_cents: null,
        output_price_per_1k_cents: null,
      }));
    return [...catEntries, ...orphans];
  });

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    const payload: PartnerAISettingsUpdate = {
      allowed_model_ids: unrestricted() ? null : Array.from(selectedIds()),
      default_model_id: defaultModelId().trim() || null,
      monthly_inference_budget_cents: budgetCents().trim() === "" ? null : Number(budgetCents()),
    };
    try {
      await update.mutateAsync(payload);
      setHydrated(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div>
          <h3 class="card-title text-base">Partner AI model allowlist</h3>
          <p class="text-xs opacity-60 mt-0.5">
            Set the model pool every workspace under this partner can pick from. Workspaces narrow
            this further on their own AI settings page; the effective pool is the intersection.
          </p>
        </div>

        <Show when={settings.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={catalog.data && !catalog.data.scaigrid_configured}>
          <div role="alert" class="alert alert-warning text-sm mt-2">
            ScaiGrid isn't configured on this deployment. Set
            <code class="ml-1">SCAICRM_SCAIGRID_API_KEY</code> to enable AI.
          </div>
        </Show>

        <Show when={!unrestricted() && selectedIds().size === 0}>
          <div role="alert" class="alert alert-warning text-sm mt-2">
            Allowlist is empty — AI will be disabled for every workspace under this partner. Tick
            "No partner-tier restriction" to inherit ScaiGrid's full catalogue instead.
          </div>
        </Show>

        <div class="form-control mt-3">
          <label class="label cursor-pointer justify-start gap-2">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              checked={unrestricted()}
              onChange={(e) => setUnrestricted(e.currentTarget.checked)}
            />
            <span class="label-text">
              No partner-tier restriction (workspaces may use any model in the ScaiGrid catalogue)
            </span>
          </label>
        </div>

        <Show when={!unrestricted()}>
          <div class="mt-2 max-h-72 overflow-y-auto border border-base-300 rounded-md">
            <Show
              when={allModels().length > 0}
              fallback={
                <p class="p-3 text-sm opacity-60">
                  No models in catalog. Check ScaiGrid configuration.
                </p>
              }
            >
              <For each={allModels()}>
                {(m) => (
                  <label class="flex items-start gap-2 p-2 border-b border-base-200 last:border-b-0 cursor-pointer hover:bg-base-200">
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm mt-1"
                      checked={selectedIds().has(m.id)}
                      onChange={() => toggle(m.id)}
                    />
                    <div class="flex-1 text-sm">
                      <div class="font-medium">{m.label}</div>
                      <div class="text-xs opacity-60">
                        <code>{m.id}</code>
                        <Show when={m.vendor}>
                          <span class="ml-2">· {m.vendor}</span>
                        </Show>
                        <Show when={m.context_window}>
                          <span class="ml-2">· {m.context_window!.toLocaleString()} ctx</span>
                        </Show>
                      </div>
                    </div>
                  </label>
                )}
              </For>
            </Show>
          </div>
        </Show>

        <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="label" for="partner-ai-default-model">
              <span class="label-text">Default model (partner-tier hint)</span>
            </label>
            <select
              id="partner-ai-default-model"
              class="select select-bordered select-sm w-full"
              value={defaultModelId()}
              onChange={(e) => setDefaultModelId(e.currentTarget.value)}
            >
              <option value="">— none —</option>
              <For
                each={
                  unrestricted() ? allModels() : allModels().filter((m) => selectedIds().has(m.id))
                }
              >
                {(m) => <option value={m.id}>{m.label}</option>}
              </For>
            </select>
            <p class="text-xs opacity-60 mt-1">Workspaces override this with their own choice.</p>
          </div>
          <div>
            <label class="label" for="partner-ai-budget">
              <span class="label-text">Monthly inference budget (cents)</span>
            </label>
            <input
              id="partner-ai-budget"
              type="number"
              min="0"
              class="input input-bordered input-sm w-full"
              placeholder="Unlimited"
              value={budgetCents()}
              onInput={(e) => setBudgetCents(e.currentTarget.value)}
            />
            <p class="text-xs opacity-60 mt-1">
              Reserved — enforcement lands when the budget layer ships.
            </p>
          </div>
        </div>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="flex justify-end mt-3">
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
